import { Dimensions, MarketplaceOrderApiModel, SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import {
  BuyerNote,
  DeliveryInstructions,
  DeliverySafePlace,
  EoriNumber,
  ExportReason,
  GSP,
  IossNumber,
  MarketplaceService,
  Prime
} from '../../lib/consts/ItemMetaConstants';
import { SumNumber } from '../../lib/helpers/ArrayHelper';
import { toIso3Code } from '../../lib/helpers/IsoCodeHelper';
import { postcodeHelper } from '../../lib/helpers/PostcodeHelper';
import { GetFieldData, GetFieldDataExtra, GetHeaders, GetMatchedHeaderName } from './GetFieldData';
import { LocalUnitsEntry, toMetricDimensionCm, toMetricWeightKg } from '../../lib/helpers/DimensionUnits';
import { reconcileLocalUnitsEntry } from '../../lib/helpers/LocalUnitsEntryHelper';
import {
  CoverExtra,
  DeliveredDutyPaidExtra,
  DeliveryGuaranteeExtra,
  PrintInStoreExtra,
  SignatureExtra,
  SmsExtra
} from '../ExtrasHelper';
import { FindCountryName } from '../../lib/cache/CountryCache';
import { SmartSendProvider } from '~/lib/consts/providers';

function sanitiseNumber(v: any, d: number): number {
  if (!v || v === 'NULL') {
    return d;
  }
  try {
    return parseFloat(v);
  } catch {
    return d;
  }
}

export interface CsvSmartSendMapResult {
  raw: string[];
  item: SmartSendItemApiModel;
}

export function MapCsvDataToSmartSendItem(
  data: Array<any>,
  defaultCountry: string,
  websiteUnits: 'imperial' | 'metric'
): CsvSmartSendMapResult[] {
  const output: { raw: string[]; item: SmartSendItemApiModel }[] = [];
  const headers = GetHeaders(data);

  for (let index = 1; index < data.length; index++) {
    const rowData = data[index].data;

    const country = GetFieldData('country', headers, rowData) || defaultCountry;
    const contents = GetFieldData('item name', headers, rowData) || GetFieldData('reference', headers, rowData);
    const ref = GetFieldData('reference', headers, rowData);

    const rawWeight = sanitiseNumber(GetFieldData('weight', headers, rowData), 0);
    const rawHeight = sanitiseNumber(GetFieldData('height', headers, rowData), 0);
    const rawWidth = sanitiseNumber(GetFieldData('width', headers, rowData), 0);
    const rawLength = sanitiseNumber(GetFieldData('length', headers, rowData), 0);

    // The matched weight column's own header can override the sheet-wide `units` setting: a
    // header literally named/containing "kg" is always metric, and "lb" is always imperial -
    // independent of what the rest of the sheet is in. Any other weight header (weight, wght,
    // Item Weight) keeps following `units`, same as length/width/height (unaffected below).
    const weightHeaderName = GetMatchedHeaderName('weight', headers)?.toLowerCase() ?? '';
    const weightIsImperial = weightHeaderName.includes('kg')
      ? false
      : weightHeaderName.includes('lb')
        ? true
        : websiteUnits === 'imperial';

    const metricDimensions: Dimensions = {
      Weight: weightIsImperial ? toMetricWeightKg(rawWeight) : rawWeight,
      Height: websiteUnits === 'imperial' ? toMetricDimensionCm(rawHeight) : rawHeight,
      Width: websiteUnits === 'imperial' ? toMetricDimensionCm(rawWidth) : rawWidth,
      Length: websiteUnits === 'imperial' ? toMetricDimensionCm(rawLength) : rawLength
    };

    // Record what the customer actually put in the CSV (in their own unit system) so it displays
    // as an as-entered imperial value rather than being silently re-derived from the rounded metric
    // conversion above - same as a manual imperial entry. Fields left blank in the CSV (0) aren't
    // "as entered", so they're omitted rather than recorded as a bogus 0 original. The entry is only
    // ever recorded when the sheet's overall unit setting is imperial - a "lb"-named weight column
    // still converts correctly even on a metric-flagged sheet, but isn't recorded as as-entered
    // unless the user actually selected imperial for the import.
    const localUnitsEntry: LocalUnitsEntry | undefined =
      websiteUnits === 'imperial'
        ? {
            unitSystem: 'imperial',
            originalWeightLb: weightIsImperial ? rawWeight || undefined : undefined,
            originalLengthIn: rawLength || undefined,
            originalWidthIn: rawWidth || undefined,
            originalHeightIn: rawHeight || undefined
          }
        : undefined;

    const item = {
      Status: 'Active',
      MarketplaceStatus: 'Sold',
      Recipient: postcodeHelper({
        CountryIsoCode: country.length == 2 ? toIso3Code(country) : country,
        Name:
          GetFieldData('customer', headers, rowData) ||
          `${GetFieldData('first name', headers, rowData)} ${GetFieldData('last name', headers, rowData)}`,
        Email: GetFieldData('email', headers, rowData),
        Phone: GetFieldData('telephone', headers, rowData),
        AddressLine1: GetFieldData('property', headers, rowData),
        AddressLine2: GetFieldData('street', headers, rowData),
        Locality: GetFieldData('locality', headers, rowData),
        Town: GetFieldData('town', headers, rowData) || GetFieldData('street', headers, rowData),
        County: GetFieldData('county', headers, rowData),
        VatNumber: GetFieldData('vatnumber', headers, rowData),
        Postcode: GetFieldData('postcode', headers, rowData),
        Organisation: GetFieldData('organisation', headers, rowData),
        Meta: {},
        Provider: SmartSendProvider
      }),
      Tags: (GetFieldData('tags', headers, rowData)?.split(',') || []).filter((x) => !!x),
      Sender: { Meta: {}, VatNumber: GetFieldData('sender-vatnumber', headers, rowData) },
      PackageDimensions: metricDimensions,
      Source: { Name: 'CSV', Id: 'csv' },
      Description: contents,
      MarketplaceOrderLines: [
        {
          Source: { Name: 'CSV', Id: 'csv' },
          MarketplaceOrderId: ref == contents ? '' : ref,
          CustomerReference: ref == contents ? '' : ref,
          InternalReference: ref == contents ? '' : ref,
          Quantity: sanitiseNumber(GetFieldData('quantity', headers, rowData), 1),
          Value: sanitiseNumber(GetFieldData('value', headers, rowData), 20),
          Product: {
            Description: contents,
            TariffCode: GetFieldData('tariffCode', headers, rowData),
            Meta: {},
            CountryOfManufacture: FindCountryName(GetFieldData('origincountry', headers, rowData)),
            Sku: GetFieldData('sku', headers, rowData)
          },
          Meta: {}
        } as MarketplaceOrderApiModel
      ] as MarketplaceOrderApiModel[],
      Meta: {},
      Extras: []
    } as SmartSendItemApiModel;

    // Email
    if (item.Recipient?.Email) {
      item.Recipient.Email = item.Recipient.Email.toLowerCase();
    }
    if (item.Sender?.Email) {
      item.Sender.Email = item.Sender.Email.toLowerCase();
    }

    // Assign Sum
    item.Value = SumNumber(item.MarketplaceOrderLines.map((x) => x.Value));

    // Extras
    if (GetFieldDataExtra('sms', headers, rowData)) {
      item.Extras.push({ Id: SmsExtra, Name: 'Sms' });
    }

    if (GetFieldDataExtra('cover', headers, rowData)) {
      item.Extras.push({ Id: CoverExtra, Name: 'Cover' });
    }

    if (GetFieldDataExtra('signature', headers, rowData)) {
      item.Extras.push({ Id: SignatureExtra, Name: 'Signature' });
    }

    if (GetFieldDataExtra('delivery guarantee', headers, rowData)) {
      item.Extras.push({ Id: DeliveryGuaranteeExtra, Name: 'Delivery Guarantee' });
    }

    if (GetFieldDataExtra('delivered duty paid', headers, rowData)) {
      item.Extras.push({ Id: DeliveredDutyPaidExtra, Name: 'Delivered Duty Paid' });
    }

    if (GetFieldDataExtra('pis', headers, rowData)) {
      item.Extras.push({ Id: PrintInStoreExtra, Name: 'Print in Store' });
    }

    // Only store if the website is set to imperial - dimensions are still as-entered imperial even
    // when a "kg"-named weight column keeps the weight itself metric (localUnitsEntry above already
    // omits originalWeightLb in that case).
    if (websiteUnits === 'imperial') {
      reconcileLocalUnitsEntry(item.Meta, localUnitsEntry);
    }

    // GSP
    if (GetFieldDataExtra('gsp', headers, rowData)) {
      item.Meta[GSP] = { Value: GetFieldData('gsp', headers, rowData), Type: 'bool' };
    }

    // Amazon Prime
    if (GetFieldDataExtra('prime', headers, rowData)) {
      item.Meta[Prime] = { Value: GetFieldData('prime', headers, rowData), Type: 'bool' };
    }

    // Recipient Specifics
    item.Recipient.Meta[EoriNumber] = { Value: GetFieldData('eori', headers, rowData), Type: 'string' };

    // Sender Specifics
    item.Sender.Meta[EoriNumber] = { Value: GetFieldData('sender-eori', headers, rowData), Type: 'string' };

    // Instuctions
    item.Meta[DeliveryInstructions] = { Value: GetFieldData('instructions', headers, rowData), Type: 'string' };
    item.Meta[DeliverySafePlace] = { Value: GetFieldData('safe place', headers, rowData), Type: 'string' };
    item.Meta[BuyerNote] = { Value: GetFieldData('buyer note', headers, rowData), Type: 'string' };

    // Service
    const s = GetFieldData('service', headers, rowData);
    if (s) {
      item.Meta[MarketplaceService] = { Value: s, Type: 'string' };
    }

    // Label Reference
    const lr = GetFieldData('labelreference', headers, rowData);
    if (lr) {
      item.Meta['LabelReference'] = { Value: lr, Type: 'string' };
    }

    // Item Specifics
    item.Meta[IossNumber] = { Value: GetFieldData('iossnumber', headers, rowData), Type: 'string' };
    item.Meta[ExportReason] = { Value: GetFieldData('exportreason', headers, rowData) || 'Sale', Type: 'string' };

    output.push({ item: item, raw: rowData });
  }

  return output;
}
